import React, {useState} from 'react';
import {Row, Col, Form} from 'react-bootstrap';
import Button from '@/components/ui/Button';
import {useStoreActions} from 'easy-peasy';
import {useAlert} from '@/components/alerts';
import * as yup from 'yup';
import axios from 'axios';
import HTMLRichTextEditor from '@/components/rich_text_editor/HTMLRichTextEditor';
import {buildEventURL} from '@/utils/urlBuilder';
import i18n from 'i18n';

interface Comment {
    id?: number;
    comment: string;
}

interface FormValues {
    comment: string;
}

interface AddNoteFormProps {
    onDone: () => void;
    eventId: number;
    existingComment?: Comment;
    mode: 'edit' | 'add';
}

const CommentFormSchema = yup.object({
    comment: yup.string()
        .max(2000, i18n.t('jt_max_length.text', {field: 'comment', length: 2000}))
        .required(i18n.t('jt_required_field_with_specific_field.text', {field: 'comment'})),
});

const AddNoteForm: React.FC<AddNoteFormProps> = React.memo(({
    onDone,
    eventId,
    existingComment,
    mode,
}) => {
    const {showSuccess} = useAlert();
    const storeEventComments = useStoreActions((state: any) => state.eventDetail.setEventComments);

    const [commentValue, setCommentValue] = useState<string>(existingComment?.comment || '');
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const resourceUrl = buildEventURL('eventComments', eventId);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        try {
            // Local validation
            await CommentFormSchema.validate({comment: commentValue}, {abortEarly: false});
        } catch (validationErr: any) {
            if (validationErr?.errors?.length) {
                setError(validationErr.errors[0]);
            } else {
                setError(i18n.t('invalid_data.text'));
            }
            return;
        }

        try {
            setSubmitting(true);

            let response;
            if (mode === 'edit' && existingComment?.id) {
                // Update existing comment
                response = await axios.put(
                    `${resourceUrl}/${existingComment.id}`,
                    {comment: commentValue} as FormValues,
                );
            } else {
                // Create new comment
                response = await axios.post(
                    resourceUrl,
                    {comment: commentValue} as FormValues,
                );
            }

            // Update store with new comments list
            storeEventComments(response.data);

            // Show success toast
            const displayActionName = mode === 'edit' ? 'edited' : 'added';
            showSuccess(i18n.t('jt_comment_success.text', {action: displayActionName}));

            // Close modal
            onDone();
        } catch (err: any) {
            if (err?.response?.status === 400) {
                setError(err.response?.data?.message ?? i18n.t('invalid_data.text'));
            } else {
                setError(i18n.t('jt_something_went_wrong.text'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Form noValidate onSubmit={handleSubmit}>
            <Row>
                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="eventComment">
                        <HTMLRichTextEditor
                            key={existingComment?.id ?? 'new-comment'}
                            HTMLValue={commentValue}
                            onChange={(HTMLDump: string) => {
                                setCommentValue(HTMLDump);
                            }}
                            customHeight={{minHeight: '250px'}}
                        />
                        {error && (
                            <div className="invalid-feedback d-block">
                                {error}
                            </div>
                        )}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="eventComment-save">
                        <Button
                            variant="info"
                            fullWidth
                            type="submit"
                            data-cy="note-modal"
                            disabled={submitting}
                            loading={submitting}
                        >
                            {i18n.t('jt_save.text')}
                        </Button>
                    </Form.Group>
                </Col>
            </Row>
        </Form>
    );
});

export default AddNoteForm; 